"use client";

import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Code2, BarChart3 } from "lucide-react";

/**
 * 3D-Bühne im Hintergrund + gestochen scharfer Live-Wechsel zwischen
 * Markdown-Code (`<Chart />`) und dem fertigen leuchtenden Balkendiagramm!
 */
export default function Preview() {
  const [rendered, setRendered] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setRendered((v) => !v), 2400);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      aria-hidden="true"
      className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-2xl p-4 select-none"
      style={{ perspective: "900px" }}
    >
      <motion.div
        animate={{ scale: [1, 1.22, 1], opacity: [0.45, 0.85, 0.45] }}
        transition={{ duration: 4.5, repeat: Infinity }}
        className="pointer-events-none absolute left-1/2 top-1/2 h-40 w-56 -translate-x-1/2 -translate-y-1/2 rounded-full bg-emerald-500/30 blur-3xl"
      />

      <div
        className="pointer-events-none absolute inset-x-[-20%] bottom-[-35%] h-[110%] opacity-45 dark:opacity-60"
        style={{
          transform: "rotateX(68deg)",
          maskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
        }}
      >
        <motion.div
          animate={{ backgroundPosition: ["0px 0px", "0px 28px"] }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="h-full w-full bg-[linear-gradient(to_right,rgba(16,185,129,0.3)_1px,transparent_1px),linear-gradient(to_bottom,rgba(16,185,129,0.3)_1px,transparent_1px)] bg-[size:28px_28px]"
        />
      </div>

      {/* Gestochen scharfer Vordergrund */}
      <div className="relative z-10 flex w-56 flex-col items-center gap-2.5 antialiased">
        <AnimatePresence mode="wait">
          {!rendered ? (
            <motion.div
              key="mdx-code"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="flex w-full flex-col gap-1.5 rounded-2xl border border-sky-400/70 bg-white p-3.5 shadow-[0_14px_28px_-6px_rgba(14,165,233,0.45)] dark:bg-zinc-900"
            >
              <span className="flex items-center gap-1.5 font-mono text-[10px] font-bold text-sky-500">
                <Code2 className="h-3.5 w-3.5" />
                content.mdx
              </span>
              <p className="font-mono text-xs font-black text-neutral-900 dark:text-white">
                # Bericht 2026
              </p>
              <p className="rounded bg-sky-500/15 px-2 py-0.5 font-mono text-[11px] font-bold text-sky-600 dark:text-sky-300">
                &lt;LiveChart /&gt;
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="mdx-rendered"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="flex w-full flex-col gap-2 rounded-2xl border border-emerald-400/80 bg-white p-3.5 shadow-[0_16px_32px_-6px_rgba(16,185,129,0.55)] dark:bg-zinc-900"
            >
              <div className="flex items-center justify-between">
                <span className="font-sans text-xs font-black text-neutral-900 dark:text-white">
                  Bericht 2026
                </span>
                <BarChart3 className="h-3.5 w-3.5 text-emerald-500" />
              </div>
              <div className="flex h-9 items-end gap-2 pt-1">
                {[45, 80, 60, 100, 75].map((h, i) => (
                  <motion.div
                    key={i}
                    initial={{ height: 4 }}
                    animate={{ height: `${h}%` }}
                    transition={{
                      type: "spring",
                      stiffness: 260,
                      damping: 18,
                      delay: i * 0.04,
                    }}
                    className="flex-1 rounded-t bg-gradient-to-t from-emerald-600 to-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.5)]"
                  />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
